import { useEffect, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { photoUrl } from "@/lib/photos";

export default function Hero({ property }) {
  const { t, lang } = useLang();
  const tagline = lang === "es" ? property.tagline_es : property.tagline_en;
  const [bgUrl, setBgUrl] = useState(property.hero_image);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/photos?property_id=${property.id}`);
        if (cancelled) return;
        const portada = data?.sections?.portada;
        if (portada && portada.length > 0) {
          setBgUrl(photoUrl(portada[0]));
        }
      } catch {
        // keep fallback
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [property.id, property.hero_image]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center scale-105"
        style={{ backgroundImage: `url(${bgUrl})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-stone-900/40 via-stone-900/20 to-stone-900/80" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-24 min-h-[100vh] flex flex-col justify-end">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E07A5F] text-white text-eyebrow mb-8 fade-up">
            <i className="fa-solid fa-tag text-[10px]" />
            {t("hero.badge")}
          </div>
          <p className="text-eyebrow text-stone-200 mb-4 fade-up fade-up-delay-1" data-testid="hero-eyebrow">
            {t("hero.eyebrow")}
          </p>
          <h1
            data-testid="hero-title"
            className="font-serif-display text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-6 fade-up fade-up-delay-2"
          >
            {property.name.split(" ").slice(0, -2).join(" ")}{" "}
            <span className="block italic font-light text-[#F2CC8F]">{property.name.split(" ").slice(-2).join(" ")}</span>
          </h1>
          <p className="font-serif-display italic text-stone-100 text-xl md:text-2xl mb-4 fade-up fade-up-delay-3">
            {tagline}
          </p>
          <p className="text-stone-100 text-lg max-w-2xl mb-10 leading-relaxed fade-up fade-up-delay-3">
            {t("hero.sub")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 fade-up fade-up-delay-4">
            <a
              href="#reservar"
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-2 bg-[#E07A5F] hover:bg-[#D3674B] text-white px-8 py-4 text-sm uppercase tracking-[0.18em] font-semibold transition-all hover:-translate-y-0.5"
            >
              {t("hero.cta_primary")}
              <i className="fa-solid fa-arrow-right text-xs" />
            </a>
            <a
              href="#la-casa"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-stone-200/80 text-stone-50 hover:bg-stone-50 hover:text-stone-900 px-8 py-4 text-sm uppercase tracking-[0.18em] font-semibold transition-all"
            >
              {t("hero.cta_secondary")}
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 right-8 md:bottom-12 md:right-12 z-10 text-stone-100 text-xs tracking-[0.3em] uppercase rotate-90 origin-bottom-right hidden md:block">
        Tuxpan · Veracruz · MX
      </div>
    </section>
  );
}
